import React, { ChangeEvent } from 'react';
import IPaymentDetails from './IPaymentDetails';
import Big from 'big.js';
import AsyncImage from '@appComponents/AsyncImage/AsyncImage';
import CurrencyElement from '@appComponents/CurrencyElement/CurrencyElement';
import { numberWithDelimiter } from '@features/utils/functions';
import { CurrencyUpper, CURRENCY } from '@market/features/const.market';
import { MAIN_CURRENCY } from '@bundles/consts';
import { classNames as cn } from '@features/utils/reactFunctions';
import ToolTip from '@appComponents/ToolTip/ToolTip';
import noPhotoMale from '@images/no_photo_male.png';
import noPhotoCompany from '@images/no_photo_company.png';

import * as styles from './PaymentDetailsStyle.module.scss';
import * as elements from '@appStyles/_elements.module.scss';
const big = Big();

const PaymentDetails: React.FunctionComponent<IPaymentDetails> = (props) => {
  const {
    isSellPage,
    spv,
    orderedNumberOfShares,
    orderedPricePerShare,
    totalValue,
    updateState,
    paymentDetails,
    bftFeeDiscountPercent,
  } = props;

  const displayCompanyInfo = () => (
    <div className={styles.companyInfo}>
      <AsyncImage image={spv.company_avatar.url} fallback={noPhotoCompany}>
        <div className={styles.avatar} />
      </AsyncImage>
      <p className={styles.name}><strong>{spv.name}</strong><span>{spv.company_name}</span></p>
    </div>
  );

  const displayOrderedShares = () => (
    <div className={styles.orderInfo}>
      <p className={styles.orderInfoShares}>{numberWithDelimiter(Number(orderedNumberOfShares))} Shares</p>
      <p className={styles.orderInfoPrice}>
        {numberWithDelimiter(Number(orderedPricePerShare))}
        <span> {CurrencyUpper} per share</span>
      </p>
    </div>
  );

  const displayValueAndFee = () => (
    <div className={styles.details}>
      <p data-cy="shares-value">Shares value:
        <CurrencyElement
          value={totalValue}
          bold={true}
          currency={MAIN_CURRENCY}
        />
      </p>
      <p data-cy="trade-fee">
        Trade fee ({paymentDetails.discountFeePercent}%)
        <span>
          {numberWithDelimiter(paymentDetails.fee)} <span>{paymentDetails.discountIsOn ? 'BFT' : CURRENCY}</span>
        </span>
      </p>
    </div>
  );

  const onToggleChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateState({
      paymentDetails: {
        ...paymentDetails,
        discountIsOn: !paymentDetails.discountIsOn,
        toggleTouched: true,
      },
    });
  };

  const displayToggleFee = () => {
    const feeText = isSellPage ? 'seller fee' : 'trade fee';

    return (
      <>
        <div className={styles.toggleHolder}>
          {displayCheckBox()}
          <p className={styles.inputDescription}>
            <b>Pay a fee with BFT and save {bftFeeDiscountPercent}% on {feeText}</b>
          </p>
        </div>
        {isSellPage && (
          <p className={styles.toggleSellerDesc}>
            We will use your BFT balance if available at the time of the trade.
            <br />
            Should there not be enough BFT available to cover the whole fee, the
            full, undiscounted fee will be collected in USD from your payout.
          </p>
        )}
      </>
    );
  };

  const displayCheckBox = () => {
    const body = (
      <div className={cn(elements.longRadio, styles.inputHolder)}>
        <input
          disabled={paymentDetails.toggleBlocked}
          type="checkbox"
          checked={paymentDetails.discountIsOn}
          id="bftDiscountInput"
          onChange={onToggleChange}
        />
        <label htmlFor="bftDiscountInput" />
      </div>
    );

    return paymentDetails.toggleBlocked
      ? (
        <ToolTip className={styles.tooltip} text="You don't have enough BFT to cover the trade fee.">
          {body}
        </ToolTip>
      ) : body;
  };

  const displayTotalValue = () => (
    <div className={styles.totalValue}>
      <div className={styles.totalValueHolder}>
        <p>{props.isSellPage ? 'To pay out' : 'Total to pay'}</p>
        {paymentDetails.discountIsOn ? (
          <div>
            <CurrencyElement value={totalValue} currency={MAIN_CURRENCY} />
            &nbsp;{isSellPage ? '-' : '+'}&nbsp;
            <CurrencyElement value={paymentDetails.fee} currency="bft" />
          </div>
        ) : (
          <CurrencyElement
            value={
              isSellPage
                ? big(totalValue).minus(paymentDetails.fee)
                : big(totalValue).plus(paymentDetails.fee)
            }
            currency={MAIN_CURRENCY}
          />
        )}
      </div>
    </div>
  );

  const displayTradePartner = () => (
    paymentDetails.tradePartner && (
      <div className={styles.partnerInfo}>
        <h4 className={styles.partnerInfoHeadline}>{isSellPage ? 'Buyer' : 'Seller'}</h4>
        <div className={styles.partnerInfoDetails}>
          <AsyncImage image={paymentDetails.tradePartner.avatar} fallback={noPhotoMale}>
            <div className={styles.avatar} />
          </AsyncImage>
          <p className={styles.name}><strong>{paymentDetails.tradePartner.selected_name}</strong></p>
        </div>
      </div>
    )
  );

  return (
    <section className={cn(styles.paymentDetails, props.isBlocked && styles.paymentDetailsBlocked)}>
      <h3>You are {isSellPage ? 'selling' : 'buying'}</h3>
      {displayCompanyInfo()}
      {displayTradePartner()}
      <hr />
      {displayOrderedShares()}
      <hr />
      {displayValueAndFee()}
      {displayToggleFee()}
      {displayTotalValue()}
    </section>
  );
};

export default PaymentDetails;
